"use client";

import { useState } from "react";
import { Mail, Copy } from "lucide-react";
import GeneratorPage from "../GeneratorPage";
import { CATEGORIES } from "@/constants/categories";
import { Button, Input, Select } from "@/components/design-system";
import { generateEmails, AVAILABLE_EMAIL_DOMAINS, GeneratedEmail } from "@/lib/emailGenerator";
import { useTranslations, useLocale } from "next-intl";
import { Controller } from "react-hook-form";
import SchemaScript from "@/components/common/SchemaScript";
import { Routes } from "@/constants/routes";
import { Locale } from "@/constants/locales";

export default function FakeEmailGenerator() {
    const t = useTranslations("emails");
    const locale = useLocale() as Locale;
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<(GeneratedEmail & { id: string })[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Default form state
    const [fallbackForm] = useState({
        domain: "",
        count: 10,
        includeNumbers: true,
        includeSpecial: false,
    });

    const handleGenerate = (e: React.FormEvent, formData?: any) => {
        e.preventDefault();
        const data = formData || fallbackForm;
        const count = Number(data.count);

        setIsGenerating(true);

        setTimeout(() => {
            const results = generateEmails({
                count,
                domain: data.domain || undefined,
                includeNumbers: data.includeNumbers !== false,
                includeSpecial: data.includeSpecial === true,
            });

            const resultsWithId = results.map(email => ({
                id: Math.random().toString(36).substr(2, 9),
                ...email
            }));

            setGenerated(resultsWithId);
            setIsGenerating(false);
        }, 400);
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const handleCopyAll = () => {
        const allText = generated.map(g => g.email).join("\n");
        navigator.clipboard.writeText(allText);
        setCopiedId("all");
        setTimeout(() => setCopiedId(null), 1500);
    };

    // Custom results renderer
    const customResultsRenderer = (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                {generated.length > 0 && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleCopyAll}
                    >
                        {copiedId === "all" ? t("results.copyAllSuccess") : t("results.copyAll")}
                    </Button>
                )}
            </div>
            <div className="grid gap-4 md:grid-cols-1">
                {generated.length === 0 ? (
                    <div className="bg-muted/30 border border-dashed border-muted-foreground/25 rounded-lg p-8 text-center min-h-[120px] flex flex-col items-center justify-center">
                        <Mail className="h-8 w-8 text-muted-foreground/50 mb-3" />
                        <span className="text-muted-foreground">{t("results.noResults")}</span>
                    </div>
                ) : (
                    generated.map((email) => (
                        <div key={email.id} className="relative bg-white dark:bg-slate-900 rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-all group">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleCopy(email.email, email.id)}
                                    className="p-2 text-muted-foreground hover:text-primary bg-background border rounded-md shadow-sm"
                                    title={t("form.copyButton")}
                                >
                                    {copiedId === email.id ? <span className="text-green-500 font-bold text-xs">{t("form.copiedButton")}</span> : <Copy className="h-4 w-4" />}
                                </button>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                                    <Mail className="h-5 w-5" />
                                </div>
                                <div className="space-y-1 flex-1">
                                    <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{email.domain}</div>
                                    <div className="font-mono text-lg text-foreground break-all">{email.email}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    // Form config
    const formConfig = {
        values: fallbackForm,
        fields: [
            {
                key: "domain",
                label: t("form.domain"),
                type: "select" as const,
                validation: { required: false },
            },
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                validation: { required: true, min: 1, max: 50 },
            },
            {
                key: "includeNumbers",
                label: t("form.includeNumbers"),
                type: "checkbox" as const,
                validation: { required: false },
            },
            {
                key: "includeSpecial",
                label: t("form.includeSpecial"),
                type: "checkbox" as const,
                validation: { required: false },
            },
        ],
        onChange: () => { },
    };

    return (
        <>
            <GeneratorPage
                title={t("title")}
                description={t("description")}
                icon={<Mail className="h-4 w-4" />}
                category={CATEGORIES.personal}
                generatedData={generated.length ? generated.map(g => g.email).join("\n") : null}
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                formConfig={formConfig}
                resultsRenderer={customResultsRenderer}
                faqs={t.raw("faqs")}
                similarGenerators={[]}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">{t("form.domain")}</label>
                        <Controller
                            name="domain"
                            render={({ field }) => (
                                <Select
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    <option value="">{t("form.domainPlaceholder")}</option>
                                    {AVAILABLE_EMAIL_DOMAINS.map(d => (
                                        <option key={d.code} value={d.code}>{d.name}</option>
                                    ))}
                                </Select>
                            )}
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.count")}</label>
                        <Controller
                            name="count"
                            rules={{ required: true, min: 1, max: 50 }}
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={field.value}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    placeholder={t("form.countPlaceholder")}
                                />
                            )}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Controller
                            name="includeNumbers"
                            render={({ field }) => (
                                <input
                                    type="checkbox"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    className="w-4 h-4"
                                />
                            )}
                        />
                        <label className="text-sm">{t("form.includeNumbers")}</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Controller
                            name="includeSpecial"
                            render={({ field }) => (
                                <input
                                    type="checkbox"
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    className="w-4 h-4"
                                />
                            )}
                        />
                        <label className="text-sm">{t("form.includeSpecial")}</label>
                    </div>
                </div>
            </GeneratorPage>

            <SchemaScript route={Routes['emails']} locale={locale} faqItems={t.raw("faqs")} />
        </>
    );
}
